import { getBrowserWindow } from '@/lib/utils/browser-window';
import { clearWindowTimeout, setWindowTimeout } from '@/lib/utils/window-timers';

const noopCleanup = () => {
  /* noop */
};

/**
 * Lightweight client-side probe to check if a URL can be embedded in an iframe.
 *
 * How it works:
 * - Creates an offscreen iframe pointing at the target URL.
 * - As soon as the browser navigates the iframe away from its initial about:blank
 *   to a cross-origin document, any access to contentDocument will throw, which
 *   we interpret as "embeddable" (navigation not blocked by XFO/CSP/mixed-content).
 * - If navigation is blocked (X-Frame-Options, CSP frame-ancestors, mixed content
 *   from https → http), the iframe typically remains on the initial same-origin
 *   about:blank document and access does not throw. After a short timeout we
 *   classify it as blocked.
 *
 * Important caveats:
 * - This is best-effort. Browsers and sites can behave differently. There is no
 *   100% reliable client-only check for arbitrary third-party URLs.
 * - We intentionally avoid reading response headers client-side (not available
 *   due to CORS). This probe mirrors what will happen when we actually embed.
 */

export type EmbedProbeStatus = 'allowed' | 'blocked' | 'timeout' | 'error';

export interface EmbedProbeResult {
  /** Optional human-readable reason for logging/analytics. */
  reason?: string;
  status: EmbedProbeStatus;
}

export interface ProbeOptions {
  /**
   * Polling interval in ms to detect cross-origin navigation. Defaults to 50ms.
   */
  pollIntervalMs?: number;
  /**
   * Total time in ms to wait before giving up. Defaults to 1500ms.
   */
  timeoutMs?: number;
}

function isValidHttpUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

export function probeIframeEmbeddability(url: string, options: ProbeOptions = {}): Promise<EmbedProbeResult> {
  const browserWindow = getBrowserWindow();
  if (!browserWindow?.document?.body) {
    return Promise.resolve({ status: 'error', reason: 'not_in_browser' });
  }

  if (!isValidHttpUrl(url)) {
    return Promise.resolve({ status: 'error', reason: 'invalid_url' });
  }

  const timeoutMs = Math.max(250, options.timeoutMs ?? 1500);
  const pollIntervalMs = Math.max(16, options.pollIntervalMs ?? 50);

  return new Promise<EmbedProbeResult>((resolve) => {
    let settledResult: EmbedProbeResult | null = null;
    let loadCheckId: number | null = null;

    const iframe = browserWindow.document.createElement('iframe');
    iframe.setAttribute(
      'sandbox',
      // Mirror the production iframe allowances for consistency
      'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
    );
    iframe.referrerPolicy = 'no-referrer';
    iframe.loading = 'eager';

    // Offscreen but still loadable in all major browsers
    const iframeStyle = iframe.style;
    iframeStyle.position = 'absolute';
    iframeStyle.left = '-99999px';
    iframeStyle.top = '-99999px';
    iframeStyle.width = '0px';
    iframeStyle.height = '0px';
    iframeStyle.opacity = '0';
    iframeStyle.pointerEvents = 'none';
    iframeStyle.border = '0';

    const cleanup = () => {
      try {
        iframe.remove();
      } catch {
        // ignore
      }
    };

    const settle = (res: EmbedProbeResult) => {
      if (settledResult !== null) {
        return;
      }
      settledResult = res;
      if (timeoutId !== null) clearWindowTimeout(timeoutId);
      if (pollId !== null) clearWindowTimeout(pollId);
      if (loadCheckId !== null) clearWindowTimeout(loadCheckId);
      cleanup();
      resolve(res);
    };

    let pollId: number | null = null;
    const startPolling = () => {
      const poll = () => {
        if (settledResult !== null) {
          return;
        }
        try {
          // Accessing contentDocument succeeds while the iframe is still on the
          // initial about:blank (same-origin). Once navigation to the target URL
          // occurs and it is cross-origin, this access throws a DOMException.
          if (iframe.contentDocument) {
            // accessing the document is enough to detect cross-origin navigation
          } // if this does NOT throw, still same-origin
        } catch {
          // Cross-origin navigation happened → treat as allowed
          settle({ status: 'allowed', reason: 'navigated_cross_origin' });
          return;
        }
        const nextId = setWindowTimeout(poll, pollIntervalMs);
        if (nextId !== null) {
          pollId = nextId;
        }
      };
      const initialId = setWindowTimeout(poll, pollIntervalMs);
      if (initialId !== null) {
        pollId = initialId;
      }
    };

    const onLoad = () => {
      if (settledResult !== null) {
        return;
      }
      // Give the browser a moment to flip the origin if it can
      loadCheckId = setWindowTimeout(() => {
        if (settledResult !== null) {
          return;
        }
        try {
          // If access still succeeds after load, we likely stayed on about:blank
          // (navigation blocked by XFO/CSP/mixed-content)
          if (iframe.contentDocument) {
            // noop: access either succeeds or throws
          }
          settle({ status: 'blocked', reason: 'xfo_or_csp_or_mixed_content' });
        } catch {
          settle({ status: 'allowed', reason: 'navigated_on_load' });
        }
      }, 50);
    };

    const onError = () => {
      if (settledResult !== null) {
        return;
      }
      // Network/DNS errors or other immediate failures
      settle({ status: 'blocked', reason: 'network_or_blocked' });
    };

    const timeoutId = setWindowTimeout(() => {
      settle({ status: 'timeout', reason: 'probe_timeout' });
    }, timeoutMs);

    iframe.addEventListener('load', onLoad, { once: true });
    iframe.addEventListener('error', onError, { once: true });

    // Begin probe
    browserWindow.document.body.append(iframe);
    startPolling();
    try {
      iframe.src = url;
    } catch {
      settle({ status: 'error', reason: 'set_src_failed' });
      return;
    }
  });
}

/**
 * Start loading an offscreen iframe for a URL and probe embeddability.
 *
 * Unlike `probeIframeEmbeddability`, this variant returns the actual iframe
 * element when embeddability is detected so callers can reparent it into
 * the visible UI without triggering a second network load.
 *
 * On 'allowed', the returned iframe remains attached to `document.body`
 * (offscreen) and the caller is responsible for inserting it into the DOM
 * where appropriate or calling `cleanup()` to remove it.
 * On 'blocked'/'timeout'/'error', the iframe is cleaned up automatically.
 */
export interface PreloadProbeResult extends EmbedProbeResult {
  cleanup: () => void;
  iframe?: HTMLIFrameElement;
}

export function preloadIframeWithProbe(url: string, options: ProbeOptions = {}): Promise<PreloadProbeResult> {
  const browserWindow = getBrowserWindow();
  if (!browserWindow?.document?.body) {
    return Promise.resolve({ status: 'error', reason: 'not_in_browser', cleanup: noopCleanup });
  }

  if (!isValidHttpUrl(url)) {
    return Promise.resolve({ status: 'error', reason: 'invalid_url', cleanup: noopCleanup });
  }

  const timeoutMs = Math.max(250, options.timeoutMs ?? 1500);
  const pollIntervalMs = Math.max(16, options.pollIntervalMs ?? 50);

  return new Promise<PreloadProbeResult>((resolve) => {
    let settledResult: PreloadProbeResult | null = null;

    const iframe = browserWindow.document.createElement('iframe');
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
    );
    iframe.referrerPolicy = 'no-referrer';
    iframe.loading = 'eager';

    const iframeStyle = iframe.style;
    iframeStyle.position = 'absolute';
    iframeStyle.left = '-99999px';
    iframeStyle.top = '-99999px';
    iframeStyle.width = '0px';
    iframeStyle.height = '0px';
    iframeStyle.opacity = '0';
    iframeStyle.pointerEvents = 'none';
    iframeStyle.border = '0';

    const cleanup = () => {
      try {
        iframe.remove();
      } catch {
        // ignore
      }
    };

    let pollId: number | null = null;
    let loadCheckId: number | null = null;

    const settle = (res: EmbedProbeResult, keepIframe: boolean) => {
      if (settledResult !== null) {
        return;
      }
      const payload: PreloadProbeResult = keepIframe ? { ...res, iframe, cleanup } : { ...res, cleanup };
      settledResult = payload;
      if (timeoutId !== null) clearWindowTimeout(timeoutId);
      if (pollId !== null) clearWindowTimeout(pollId);
      if (loadCheckId !== null) clearWindowTimeout(loadCheckId);
      if (!keepIframe) cleanup();
      resolve(payload);
    };

    const startPolling = () => {
      const poll = () => {
        if (settledResult !== null) {
          return;
        }
        try {
          // Access succeeds while still on about:blank (same-origin)
          if (iframe.contentDocument) {
            // noop: access either succeeds or throws
          }
        } catch {
          // Cross-origin navigation happened → treat as allowed
          settle({ status: 'allowed', reason: 'navigated_cross_origin' }, true);
          return;
        }
        const nextId = setWindowTimeout(poll, pollIntervalMs);
        if (nextId !== null) {
          pollId = nextId;
        }
      };
      const initialId = setWindowTimeout(poll, pollIntervalMs);
      if (initialId !== null) {
        pollId = initialId;
      }
    };

    const onLoad = () => {
      if (settledResult !== null) {
        return;
      }
      // Give the browser a moment to flip the origin if it can
      loadCheckId = setWindowTimeout(() => {
        if (settledResult !== null) {
          return;
        }
        try {
          // Still same-origin about:blank -> likely blocked
          if (iframe.contentDocument) {
            // noop: access either succeeds or throws
          }
          settle({ status: 'blocked', reason: 'xfo_or_csp_or_mixed_content' }, false);
        } catch {
          settle({ status: 'allowed', reason: 'navigated_on_load' }, true);
        }
      }, 50);
    };

    const onError = () => {
      if (settledResult !== null) {
        return;
      }
      settle({ status: 'blocked', reason: 'network_or_blocked' }, false);
    };

    const timeoutId = setWindowTimeout(() => {
      settle({ status: 'timeout', reason: 'probe_timeout' }, false);
    }, timeoutMs);

    iframe.addEventListener('load', onLoad, { once: true });
    iframe.addEventListener('error', onError, { once: true });

    browserWindow.document.body.append(iframe);
    startPolling();
    try {
      iframe.src = url;
    } catch {
      settle({ status: 'error', reason: 'set_src_failed' }, false);
    }
  });
}
